import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchAvBillingInfosCacheKey,
  FetchCacheAvBillingInfosFilters,
  FetchAvBillingInfosSort,
  FetchAvBillingInfosPage,
  FetchAvBillingInfosLimit,
  FetchAvBillingInfosGqlQuery
} from '../../avBillingInfosTypes';
import { FetchCacheAvBillingInfosQueryResponse } from '../../queries/fetchCacheAvBillingInfos.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_AV_BILLING_INFOS_FILTERS,
  INITIAL_AV_BILLING_INFOS_LIMIT,
  INITIAL_AV_BILLING_INFOS_PAGE,
  INITIAL_AV_BILLING_INFOS_SORT
} from '../../avBillingInfosConstants';

interface AvBillingInfosWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface AvBillingInfosWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface AvBillingInfosDefaultOptions {
  action?: string;
  cacheKey: FetchAvBillingInfosCacheKey;
  query: FetchAvBillingInfosGqlQuery;
  initialFilters?: FetchCacheAvBillingInfosFilters;
  initialSort?: FetchAvBillingInfosSort;
  initialPage?: FetchAvBillingInfosPage;
  initialLimit?: FetchAvBillingInfosLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type AvBillingInfosOptions = AvBillingInfosDefaultOptions &
  (
    | AvBillingInfosWithPrefetchItemOptions
    | AvBillingInfosWithoutPrefetchItemOptions
  );

const scope = 'avBillingInfos';

function useCachePaginatedAvBillingInfos<
  FetchAvBillingInfosType = FetchCacheAvBillingInfosQueryResponse
>({
  action,
  cacheKey,
  initialFilters = INITIAL_AV_BILLING_INFOS_FILTERS,
  initialSort = INITIAL_AV_BILLING_INFOS_SORT,
  initialPage = INITIAL_AV_BILLING_INFOS_PAGE,
  initialLimit = INITIAL_AV_BILLING_INFOS_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: AvBillingInfosOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchAvBillingInfosType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    avBillingInfosData: data,
    avBillingInfos: items,
    avBillingInfosError: itemsError,
    avBillingInfosErrorMessage: itemsErrorMessage,
    avBillingInfosTotalCount: itemsTotalCount,
    avBillingInfosFetched: isFetched,
    avBillingInfosLoading: isLoading,
    avBillingInfosIsPlaceholderData: isPlaceholderData,
    avBillingInfosFilters: currentFilters,
    avBillingInfosSort: currentSort,
    avBillingInfosPage: currentPage,
    avBillingInfosLimit: currentLimit,
    updateAvBillingInfoCache: updateItemCache,
    updateAvBillingInfosCache: updateItemsCache,
    filterAvBillingInfos: filterItems,
    changeAvBillingInfosFilters: changeItemsFilters,
    clearAvBillingInfosFilters: clearItemsFilters,
    sortAvBillingInfos: sortItems,
    paginateAvBillingInfos: paginateItems,
    limitAvBillingInfos: limitItems,
    prefetchAvBillingInfos: prefetchItems,
    prefetchAvBillingInfo: prefetchItem
  };
}

export default useCachePaginatedAvBillingInfos;
